/*
 *
 * EventBanner
 *
 * Banner at the top of the users' home with the next (or current) session of a corporate event: countdown with the
 * server clock, "Entrar" button for the stream and a collapsible programme of the day. Data comes from
 * GET /users/home (`events`, `serverNow`) and is refreshed through `onRefresh` (GET /events/active) only when a
 * session starts or ends, or when the tab becomes visible again. No polling.
 *
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";
import Strings from "utils/strings";

export interface LocalizedText {
	pt?: string;
	en?: string;
	es?: string;
}

export interface EventSession {
	_id: string;
	title: LocalizedText;
	speaker?: string;
	description?: LocalizedText;
	startsAt: string;
	endsAt: string;
	streamUrl?: string;
}

export interface HomeEvent {
	_id: string;
	name: LocalizedText;
	description?: LocalizedText;
	image?: string | null;
	startsAt: string;
	endsAt: string;
	sessions?: EventSession[];
}

interface EventBannerProps {
	events?: HomeEvent[] | null;
	serverNow?: string | null;
	onRefresh: () => void;
	language?: string;
}

interface Selection {
	event: HomeEvent;
	session: EventSession;
	start: number;
	end: number;
	state: "live" | "countdown";
}

const ZONE = "Europe/Lisbon";
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const ENTER_BEFORE_START = 10 * 60 * 1000;
const REFRESH_THROTTLE = 5 * 1000;

const pick = (text: LocalizedText | undefined, lang: string) =>
	(text && (text[lang as keyof LocalizedText] || text.pt)) || "";

const isHttps = (url?: string) => typeof url === "string" && url.startsWith("https://");

const pad = (value: number) => String(value).padStart(2, "0");

const toLisbon = (millis: number) => DateTime.fromMillis(millis, { zone: "utc" }).setZone(ZONE);

const format = (text: string, values: { [key: string]: string | number }) => {
	const result = Strings.formatString(text, values);
	return Array.isArray(result) ? result.join("") : result;
};

/**
 * The session to show: the one running now (earliest start if several overlap), otherwise the next one to start,
 * over every event. `null` when nothing is running or coming.
 */
export const selectSession = (events: HomeEvent[], now: number): Selection | null => {
	let live: Selection | null = null;
	let next: Selection | null = null;

	events.forEach((event) => {
		(event.sessions || []).forEach((session) => {
			const start = Date.parse(session.startsAt);
			const end = Date.parse(session.endsAt);
			if (isNaN(start) || isNaN(end)) return;

			if (start <= now && now < end) {
				if (!live || start < live.start) live = { event, session, start, end, state: "live" };
			} else if (start > now) {
				if (!next || start < next.start) next = { event, session, start, end, state: "countdown" };
			}
		});
	});

	return live || next;
};

/** "Hoje", "Amanhã" or "6 out" (Lisbon), for a session day relative to the current Lisbon day. */
const dayLabel = (start: DateTime, nowLisbon: DateTime, lang: string) => {
	const days = Math.round(start.startOf("day").diff(nowLisbon.startOf("day"), "days").days);
	if (days === 0) return Strings.events.today;
	if (days === 1) return Strings.events.tomorrow;
	return start.setLocale(lang).toFormat("d LLL").replace(/\.$/, "");
};

const EventBanner = ({ events, serverNow, onRefresh, language }: EventBannerProps) => {
	const lang = language || Strings.getLanguage() || "pt";
	const list = useMemo(() => (Array.isArray(events) ? events : []), [events]);

	// server clock: offset = serverNow - Date.now(), kept in a ref so the ticker never closes over a stale value
	const offsetRef = useRef(0);
	const [now, setNow] = useState(() => Date.now());
	const [showProgram, setShowProgram] = useState(false);

	useEffect(() => {
		const parsed = serverNow ? Date.parse(serverNow) : NaN;
		offsetRef.current = isNaN(parsed) ? 0 : parsed - Date.now();
		setNow(Date.now() + offsetRef.current);
	}, [serverNow]);

	const selection = useMemo(() => selectSession(list, now), [list, now]);
	const active = selection !== null;

	// 1 s ticker, only while there is something to show (cleared on unmount)
	useEffect(() => {
		if (!active) return undefined;
		const timer = window.setInterval(() => setNow(Date.now() + offsetRef.current), 1000);
		return () => window.clearInterval(timer);
	}, [active]);

	// one refresh at most every 5 s, whatever the trigger
	const onRefreshRef = useRef(onRefresh);
	onRefreshRef.current = onRefresh;
	const lastRefreshRef = useRef(0);
	const requestRefresh = useCallback(() => {
		const at = Date.now();
		if (at - lastRefreshRef.current < REFRESH_THROTTLE) return;
		lastRefreshRef.current = at;
		onRefreshRef.current();
	}, []);

	// the shown session started (countdown reached zero) or ended: refresh once to pick up BO changes
	const phase = selection ? `${selection.session._id}:${selection.state}` : "none";
	const previousRef = useRef<{ phase: string; list: HomeEvent[] } | null>(null);
	useEffect(() => {
		const previous = previousRef.current;
		previousRef.current = { phase, list };
		if (!previous || previous.list !== list) return; // first render, or new data from the parent
		if (previous.phase !== phase) requestRefresh();
	}, [phase, list, requestRefresh]);

	// tab back in the foreground: resync the clock and refresh
	useEffect(() => {
		const onVisibility = () => {
			if (document.visibilityState !== "visible") return;
			setNow(Date.now() + offsetRef.current);
			requestRefresh();
		};
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, [requestRefresh]);

	if (!selection) return null;

	const { event, session, start, end, state } = selection;
	const live = state === "live";
	const remaining = Math.max(0, start - now);
	const startLisbon = toLisbon(start);
	const endLisbon = toLisbon(end);
	const nowLisbon = toLisbon(now);
	const canEnter = live || remaining <= ENTER_BEFORE_START;
	const streamUrl = isHttps(session.streamUrl) ? (session.streamUrl as string) : null;
	const description = pick(session.description, lang) || pick(event.description, lang);

	// programme of the day: the sessions of this event on the same Lisbon day as the shown session
	const dayKey = startLisbon.toISODate();
	const program = (event.sessions || []).filter((item) => {
		const itemStart = Date.parse(item.startsAt);
		return !isNaN(itemStart) && toLisbon(itemStart).toISODate() === dayKey;
	});

	const renderTime = () => {
		if (live) {
			return (
				<React.Fragment>
					<span className="EventBannerBadge">{Strings.events.live}</span>
					<span className="EventBannerHours">
						{startLisbon.toFormat("HH:mm")} – {endLisbon.toFormat("HH:mm")}
					</span>
				</React.Fragment>
			);
		}

		if (remaining >= DAY) {
			const days = Math.round(startLisbon.startOf("day").diff(nowLisbon.startOf("day"), "days").days);
			return (
				<React.Fragment>
					<span className="EventBannerWhen">
						{dayLabel(startLisbon, nowLisbon, lang)} · {startLisbon.toFormat("HH:mm")}
					</span>
					<span className="EventBannerDays">
						{days === 1 ? Strings.events.dayLeft : format(Strings.events.daysLeft, { n: days })}
					</span>
				</React.Fragment>
			);
		}

		const totalSeconds = Math.floor(remaining / 1000);
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;
		return (
			<React.Fragment>
				<span className="EventBannerWhen">
					{dayLabel(startLisbon, nowLisbon, lang)} · {startLisbon.toFormat("HH:mm")}
				</span>
				<span className="EventBannerCountdown">
					{Strings.events.startsIn} <strong>{`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`}</strong>
				</span>
			</React.Fragment>
		);
	};

	return (
		<div className={`EventBanner${live ? " --event-live" : ""}`}>
			{event.image ? (
				<img className="EventBannerImage" src={event.image} alt="" />
			) : (
				<div className="EventBannerImage --image-placeholder" />
			)}
			<div className="EventBannerBody">
				<p className="EventBannerEvent">{pick(event.name, lang)}</p>
				<h2 className="EventBannerTitle">{pick(session.title, lang)}</h2>
				{session.speaker && <p className="EventBannerSpeaker">{session.speaker}</p>}
				{description && <p className="EventBannerDescription">{description}</p>}
				<div className="EventBannerTime">{renderTime()}</div>
				<div className="EventBannerActions">
					{streamUrl && (
						<button
							type="button"
							className="EventBannerEnter"
							disabled={!canEnter}
							onClick={() => window.open(streamUrl, "_blank", "noopener,noreferrer")}
						>
							{Strings.events.enter}
						</button>
					)}
					{program.length > 1 && (
						<button
							type="button"
							className="EventBannerToggle"
							aria-expanded={showProgram}
							onClick={() => setShowProgram(!showProgram)}
						>
							{showProgram ? Strings.events.hideProgram : Strings.events.showProgram}
						</button>
					)}
				</div>
				{showProgram && program.length > 1 && (
					<ul className="EventBannerProgram">
						{program.map((item) => {
							const itemStart = Date.parse(item.startsAt);
							const itemEnd = Date.parse(item.endsAt);
							const current = item._id === session._id;
							const done = !isNaN(itemEnd) && itemEnd <= now;
							return (
								<li
									key={item._id}
									className={`${current ? "--session-current" : ""}${done ? " --session-done" : ""}`}
								>
									<span className="EventBannerProgramHour">
										{toLisbon(itemStart).toFormat("HH:mm")}
									</span>
									<span className="EventBannerProgramTitle">{pick(item.title, lang)}</span>
									{item.speaker && <span className="EventBannerProgramSpeaker">{item.speaker}</span>}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</div>
	);
};

export default EventBanner;
